<template>
  <Transition :name="transitionName">
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div v-if="isVisible"
         :class="toastClasses"
         role="status"
         tabindex="0"
         @mouseenter="pauseTimer"
         @mouseleave="resumeTimer"
         @focusin="pauseTimer"
         @focusout="resumeTimer">
      <utd-notification :notification-mode="notificationMode"
                        :has-close-notification="hasCloseButton"
                        @closed="close">
        <template v-if="title" #title>
          {{ title }}
        </template>
        <slot>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="message" />
        </slot>
      </utd-notification>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import PubSub from '_acaSrc/utility/PubSub';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';

const props = defineProps({
    notificationMode: {
        type: String,
        default: 'success',
        validator: (value) => [ 'success', 'error', 'warning', 'info' ].includes(value)
    },
    title: {
        type: String,
        default: ''
    },
    message: {
        type: String,
        default: ''
    },
    autoHideDelay: {
        type: Number,
        default: 3000
    },
    pauseOnModalOpen: {
        type: Boolean,
        default: true
    },
    position: {
        type: String,
        default: 'top-left',
        validator: (value) => {
            return [ 'top-left', 'top-right', 'bottom-left', 'bottom-right' ].includes(value);
        }
    },
    hasCloseButton: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits([ 'closed', 'shown' ]);

const pubsub = new PubSub();
const isVisible = ref(false);
let hideTimer = null;
let modalOpen = false;

const toastClasses = computed(() => {
    const baseClass = 'utd-toast';
    return `${baseClass} ${baseClass}--${props.position}`;
});

const transitionName = computed(() => {
    return props.position.startsWith('top') ? 'toast-slide-top' : 'toast-slide-bottom';
});

const show = () => {
    isVisible.value = true;
    emit('shown');
    startTimer();
};

const close = () => {
    isVisible.value = false;
    clearTimer();
    emit('closed');
};

const handleModalOpen = () => {
    if (!props.pauseOnModalOpen) {
        return;
    }
    modalOpen = true;
    pauseTimer();
};

const handleModalClose = () => {
    if (!props.pauseOnModalOpen) {
        return;
    }
    modalOpen = false;
    if (isVisible.value) {
        startTimer();
    }
};

onMounted(() => {
    if (props.pauseOnModalOpen) {
        pubsub.subscribe('utd-modal-opened', handleModalOpen);
        pubsub.subscribe('utd-modal-closed', handleModalClose);
    }
});

onUnmounted(() => {
    if (props.pauseOnModalOpen) {
        pubsub.unsubscribe('utd-modal-opened');
        pubsub.unsubscribe('utd-modal-closed');
    }
    clearTimer();
});

const startTimer = () => {
    clearTimer();
    // Don't auto-hide toast while modal is open to prevent user from missing the message
    if (modalOpen) {
        return;
    }
    hideTimer = setTimeout(() => {
        close();
    }, props.autoHideDelay);
};

const pauseTimer = () => {
    clearTimer();
};

const resumeTimer = () => {
    startTimer();
};

const clearTimer = () => {
    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
};

defineExpose({ show, close });
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@keyframes toast-slide-in-top {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes toast-slide-out-top {
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(-100%);
    opacity: 0;
  }
}

@keyframes toast-slide-in-bottom {
  from {
    transform: translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes toast-slide-out-bottom {
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(100%);
    opacity: 0;
  }
}

.utd-toast {
  position: fixed;
  z-index: @ZINDEX-MODAL-OVERLAY-REG-MERGE;
  max-width: 600px;
  width: calc(100% - 16px);

  :deep(.utd-notification) {
    margin-top: 0;
  }
}

.utd-toast--top-left {
  top: auto;
  bottom: 16px;
  left: 8px;
}

.utd-toast--top-right {
  top: auto;
  bottom: 16px;
  right: 8px;
}

.utd-toast--bottom-left {
  bottom: 16px;
  left: 8px;
}

.utd-toast--bottom-right {
  bottom: 16px;
  right: 8px;
}

.toast-slide-bottom-enter-active {
  animation: toast-slide-in-bottom 0.3s ease-out;
}

.toast-slide-bottom-leave-active {
  animation: toast-slide-out-bottom 0.3s ease-in;
}

.toast-slide-top-enter-active {
  animation: toast-slide-in-top 0.3s ease-out;
}

.toast-slide-top-leave-active {
  animation: toast-slide-out-top 0.3s ease-in;
}

@media screen and (min-width: 768px) {
  .utd-toast--top-left {
    top: 65px;
    bottom: auto;
    left: 16px;
    width: 350px;
  }

  .utd-toast--top-right {
    top: 65px;
    bottom: auto;
    right: 16px;
    width: 350px;
  }
}
</style>
